/**
 * Depth-contour highlight — draws a soft accent halo along the chosen depth
 * contour(s) so a tapped isobath stands out while piloting. It mirrors the
 * clearing-depth highlight (per-cell DEPCNT line layers filtered by the
 * contour's charted depth value, attrs.depthM = VALDCO), but in an accent
 * colour with a wider, blurred, semi-transparent line, so it reads as emphasis
 * rather than the red do-not-cross of a clearing depth. With no values it
 * matches nothing (hidden).
 */
import type { Map as MaplibreMap, FilterSpecification } from 'maplibre-gl';
import type { Palette } from '@/store';
import { getPalette } from './style/palettes';

/** The first overlay layer id, so the halo sits under the boat/route/pilotage. */
function firstOverlayLayerId(map: MaplibreMap): string | undefined {
  const layers = map.getStyle()?.layers ?? [];
  for (const l of layers) {
    if (/^(ownship|route|pilotage|marks|safety|tide|objects)/.test(l.id)) return l.id;
  }
  return undefined;
}

function filterFor(values: number[]): FilterSpecification {
  if (!values.length) {
    return ['==', ['get', 'objClass'], '___acp_no_contour_highlight___'] as FilterSpecification;
  }
  return [
    'all',
    ['==', ['get', 'objClass'], 'DEPCNT'],
    ['in', ['to-number', ['coalesce', ['get', 'depthM', ['get', 'attrs']], -9999]], ['literal', values]],
  ] as FilterSpecification;
}

/**
 * Ensure each cell source has its contour-highlight layer and set its filter to
 * the active values. Safe to call repeatedly; it adds the layer once per source
 * and just updates the filter after.
 */
export function updateContourHighlight(
  map: MaplibreMap,
  values: number[],
  palette: Palette,
): void {
  // Style-spec-ready gate (not isStyleLoaded(), which the running sim keeps false).
  const style = map.getStyle();
  if (!style?.sources) return;
  const colour = getPalette(palette).CHMGD;
  const beforeId = firstOverlayLayerId(map);
  const filter = filterFor(values);
  for (const srcId of Object.keys(style.sources)) {
    if (!srcId.startsWith('cell-')) continue;
    const layerId = `${srcId}-conthl`;
    if (map.getLayer(layerId)) {
      map.setFilter(layerId, filter);
    } else {
      map.addLayer(
        {
          id: layerId,
          type: 'line',
          source: srcId,
          filter,
          paint: {
            'line-color': colour,
            'line-width': ['interpolate', ['linear'], ['zoom'], 8, 3, 14, 6],
            'line-opacity': 0.55,
            'line-blur': 1.5,
          },
        },
        beforeId,
      );
    }
  }
}
